import api from "@/api/v1-jwt/config/api";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import paymentMethodQueryKeys from "./query-keys";
import { PaymentMethod, PaymentMethodSchema } from "./types";
import { z } from "zod";

type PaymentMethodsQueryKey = typeof paymentMethodQueryKeys.all;

const getPaymentMethods = async (userId: string): Promise<PaymentMethod[]> => {
  const url = `/platform/payment_methods/${userId}?move_money=false`;
  const { data } = await api.get(url);
  const parsedData = z.array(PaymentMethodSchema).safeParse(data.data);
  if (!parsedData.success) {
    console.log("PAYMENT_METHOD_DATA_RESPONSE_ERROR:: ", parsedData.error);
  }
  return data.data;
};

export const usePaymentMethodsOptions = <TSelected = PaymentMethod[]>(
  userId: string,
  options?: Omit<
    UseQueryOptions<PaymentMethod[], Error, TSelected, PaymentMethodsQueryKey>,
    "queryKey" | "queryFn"
  >
): UseQueryOptions<
  PaymentMethod[],
  Error,
  TSelected,
  PaymentMethodsQueryKey
> => {
  const defaultOptions: UseQueryOptions<
    PaymentMethod[],
    Error,
    TSelected,
    PaymentMethodsQueryKey
  > = {
    queryKey: paymentMethodQueryKeys.all,
    queryFn: () => getPaymentMethods(userId),
    staleTime: 2 * 60 * 60 * 1000,
    enabled: options?.enabled ?? !!userId,
  };
  return { ...defaultOptions, ...options };
};

export const usePaymentMethods = <TSelected = PaymentMethod[]>(
  userId: string,
  options?: Omit<
    UseQueryOptions<PaymentMethod[], Error, TSelected, PaymentMethodsQueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const queryOptions = usePaymentMethodsOptions<TSelected>(userId, options);
  return useQuery<PaymentMethod[], Error, TSelected, PaymentMethodsQueryKey>({
    ...queryOptions,
  });
};
